// src/pages/admin/AdminResources.jsx â€” FINAL FULL
// (Car + Motorcycle + Desk, dropdown tidak lompat ke atas)
// + Optional: show "Booked By" (active overlap within Nowâ†’+2h) without breaking existing behavior

import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../../lib/api.js';
import toast from 'react-hot-toast';
import Card from '../../components/Card.jsx';
import Button from '../../components/Button.jsx';
import Input from '../../components/Input.jsx';
import LoadingSpinner from '../../components/LoadingSpinner.jsx';
import Modal from '../../components/Modal.jsx';
import ConfirmDialog from '../../components/ConfirmDialog.jsx';
import {
  Building,
  Plus,
  CreditCard as Edit3,
  Trash2,
  MoreVertical,
  Car,
  Monitor,
  Eye,
  EyeOff,
  Bike,
  RefreshCcw,
  Unlock,
  User,
  Clock,
} from 'lucide-react';

/* ===================== Util waktu (LOKAL, sama seperti Booking.jsx) ===================== */
const pad = (n) => String(n).padStart(2, '0');
const toSqlLocal = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  const Y = x.getFullYear();
  const M = pad(x.getMonth() + 1);
  const D = pad(x.getDate());
  const h = pad(x.getHours());
  const m = pad(x.getMinutes());
  const s = pad(x.getSeconds());
  return `${Y}-${M}-${D} ${h}:${m}:${s}`;
};
const ceilMinute = (d = new Date()) => {
  const x = new Date(d);
  x.setSeconds(0, 0);
  return x;
};
const addHours = (d, h) => {
  const x = new Date(d);
  x.setHours(x.getHours() + h);
  return x;
};
const startOfLocalDay = (d = new Date()) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const endOfLocalDay = (d = new Date()) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};// [aStart,aEnd) vs [bStart,bEnd)
const overlaps = (aStart, aEnd, bStart, bEnd) => aStart < bEnd && aEnd > bStart;

// parse booking time (server may send utc iso or sql)
const parseUTC = (s) => {
  if (!s) return new Date('invalid');
  if (String(s).endsWith('Z') || String(s).includes('T')) return new Date(s);
  return new Date(String(s).replace(' ', 'T') + 'Z');
};
const normalizeTimes = (b) => {
  const s = b.start_utc || b.start_iso || b.startZ || b.start_time;
  const e = b.end_utc || b.end_iso || b.endZ || b.end_time;
  return { start: parseUTC(s), end: parseUTC(e) };
};
const isCanceled = (b) => {
  const s = (b.status || '').toLowerCase();
  return s === 'canceled' || s === 'cancelled';
};
const isActiveBooking = (b, now = new Date()) => {
  if (isCanceled(b)) return false;
  const { start, end } = normalizeTimes(b);
  if (isNaN(start) || isNaN(end)) return false;
  return end > now;
};

/* ------------------------------- helpers resource naming ------------------------------- */
const pad3 = (n) => String(n).padStart(3, '0');
const tailNumber = (s = '') => {
  const m = String(s).match(/(\d+)\s*$/);
  return m ? parseInt(m[1], 10) : null;
};
const floorCodeFromName = (name = '') => {
  const m = String(name).match(/(\d+)/);
  return m ? `F${m[1]}-` : 'F-';
};

// prefix name untuk motor (M001..)
const nextMotorName = (resources = []) => {
  const motors = (resources || []).filter(
    (r) => (r?.type_name || '').toLowerCase() === 'motorcycle'
  );
  let max = 0;
  for (const m of motors) {
    const n = tailNumber(m.name);
    if (n != null) max = Math.max(max, n);
  }
  return `M${pad3(max + 1 || 1)}`;
};

function AdminResources() {
  const [resources, setResources] = useState([]);
  const [floors, setFloors] = useState([]);
  const [loading, setLoading] = useState(true);

  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, resource: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, resource: null });
  const [releaseDialog, setReleaseDialog] = useState({ open: false, booking: null, loading: false });
  const [submitting, setSubmitting] = useState(false);

  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeTab, setActiveTab] = useState('parking');
  const [searchTerm, setSearchTerm] = useState(''); // 'parking' | 'motorcycle' | 'desk'
  const [nameTouched, setNameTouched] = useState(false);

  const [resourceForm, setResourceForm] = useState({
    name: '',
    description: '',
    type: 'parking',
    floor_id: '',
    position_x: 0,
    position_y: 0,
    is_active: true,
  });

  /* ========= Booking status panel (Booked By) ========= */
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusWindow, setStatusWindow] = useState(() => {    
  const now = new Date();    
  const s = startOfLocalDay(now);    
  const e = endOfLocalDay(now);    
  return { start: s, end: e };  });
  const [rangeBookings, setRangeBookings] = useState([]); // bookings overlapping statusWindow

  useEffect(() => {
    fetchResources(); // initial load
    fetchFloors();
    refreshStatus({ silent: true }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchResources = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get('/admin/resources'); // admin: semua resource
      setResources(data || []);
    } catch (error) {
      console.error('Failed to fetch resources:', error);
      toast.error('Failed to load resources');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchFloors = async () => {
    try {
      const { data } = await api.get('/floors');
      setFloors(data || []);
    } catch (error) {
      console.error('Failed to fetch floors:', error);
    }
  };

  const refreshStatus = async ({ silent = false } = {}) => {
    if (!silent) setStatusLoading(true);
    try {
      const now = new Date();
      const s = statusWindow.start || startOfLocalDay(now);      
      const e = statusWindow.end || endOfLocalDay(now);
      const params = { start: toSqlLocal(s), end: toSqlLocal(e) };
      const res = await api.get('/bookings', { params });
      const list = Array.isArray(res.data) ? res.data : [];
      const cleaned = list.filter((b) => {
        if (!isActiveBooking(b, now)) return false;
        const { start, end } = normalizeTimes(b);
        return overlaps(s, e, start, end);
      });
      setRangeBookings(cleaned);
    } catch (err) {
      // jangan ganggu halaman
      console.error('Failed to refresh status:', err);
    } finally {
      if (!silent) setStatusLoading(false);
    }
  };

  const resetForm = () => {
    setNameTouched(false);
    setResourceForm({
      name: '',
      description: '',
      type: activeTab || 'parking',
      floor_id: '',
      position_x: 0,
      position_y: 0,
      is_active: true,
    });
  };

  /* ------ kalkulasi next desk name per floor ------ */
  const nextDeskNameByFloor = (floor_id) => {
    if (!floor_id) return '';
    const floor = floors.find((x) => String(x.id) === String(floor_id));
    const prefix = floorCodeFromName(floor?.name || '');
    const desksOnThisFloor = (resources || []).filter(
      (r) =>
        (r?.type_name || '').toLowerCase() === 'desk' &&
        String(r.floor_id) === String(floor_id)
    );
    let max = 0;
    for (const d of desksOnThisFloor) {
      const n = tailNumber(d.name);
      if (n != null) max = Math.max(max, n);
    }
    return `${prefix}${pad3(max + 1 || 1)}`;
  };

  const handleAdd = () => {
    resetForm();
    const baseType = activeTab;

    if (baseType === 'desk') {
      if (floors.length) {
        const fid = String(floors[0].id);
        setResourceForm((p) => ({
          ...p,
          type: 'desk',
          floor_id: fid,
          name: nextDeskNameByFloor(fid),
        }));
      } else {
        setResourceForm((p) => ({ ...p, type: 'desk', floor_id: '' }));
      }
    } else if (baseType === 'motorcycle') {
      // auto name for motorcycle (M001..)
      setResourceForm((p) => ({
        ...p,
        type: 'motorcycle',
        floor_id: '',
        name: nextMotorName(resources),
      }));
    } else {
      setResourceForm((p) => ({ ...p, type: 'parking', floor_id: '' }));
    }

    setAddModal(true);
  };

  // auto-isi nama saat modal Add (desk) & floor berubah (selama user belum mengetik manual)
  useEffect(() => {
    if (!addModal) return;

    // Desk: nama auto mengikuti floor
    if (resourceForm.type === 'desk') {
      if (!resourceForm.floor_id) return;
      if (nameTouched) return;
      const next = nextDeskNameByFloor(resourceForm.floor_id);
      setResourceForm((p) => ({ ...p, name: next }));
      return;
    }

    // Motorcycle: nama auto naik kalau belum disentuh user
    if (resourceForm.type === 'motorcycle') {
      if (nameTouched) return;
      const next = nextMotorName(resources);
      setResourceForm((p) => ({ ...p, name: next }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addModal, resourceForm.type, resourceForm.floor_id, resources, floors, nameTouched]);

  const handleEdit = (resource) => {
    setResourceForm({
      name: resource.name,
      description: resource.description || '',
      type: (resource.type_name || 'Parking').toLowerCase(),
      floor_id: resource.floor_id || '',
      position_x: resource.position_x || 0,
      position_y: resource.position_y || 0,
      is_active: !!resource.is_active,
    });
    setEditModal({ open: true, resource });
    setActiveDropdown(null);
    setNameTouched(true); // di mode edit anggap user atur nama
  };

  const handleDelete = (resource) => {
    setDeleteDialog({ open: true, resource });
    setActiveDropdown(null);
  };

  const toggleVisibility = async (resource) => {
    // Optimistic toggle
    setResources((prev) =>
      prev.map((r) => (r.id === resource.id ? { ...r, is_active: !r.is_active } : r))
    );
    try {
      await api.put(`/admin/resources/${resource.id}`, { is_active: !resource.is_active });
      toast.success(`Resource ${resource.is_active ? 'hidden' : 'shown'} successfully!`);
      fetchResources({ silent: true }); // sinkron tanpa spinner penuh
    } catch (error) {
      // rollback on error
      setResources((prev) =>
        prev.map((r) => (r.id === resource.id ? { ...r, is_active: resource.is_active } : r))
      );
      const message = error.response?.data?.error || 'Failed to update resource';
      toast.error(message);
    }
    setActiveDropdown(null);
  };

  // payload untuk admin endpoint
  const buildPayload = (form) => {
    const t = (form.type || '').toLowerCase();
    const isDesk = t === 'desk';
    const isMotor = t === 'motorcycle';

    return {
      name: (form.name || '').trim(),
      description: (form.description || '').trim(),
      type_name: isDesk ? 'Desk' : isMotor ? 'Motorcycle' : 'Parking',
      floor_id: isDesk ? (form.floor_id ? Number(form.floor_id) : null) : null,
      position_x: Number(form.position_x) || 0,
      position_y: Number(form.position_y) || 0,
      is_active: !!form.is_active,
    };
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // jaga-jaga bila name kosong
      let form = { ...resourceForm };
      const t = (form.type || '').toLowerCase();

      if (t === 'desk' && (!form.name || !/\d+$/.test(form.name))) {
        form.name = nextDeskNameByFloor(form.floor_id);
      }
      if (t === 'motorcycle' && (!form.name || !/\d+$/.test(form.name))) {
        form.name = nextMotorName(resources);
      }

      const payload = buildPayload(form);
      const res = await api.post('/admin/resources', payload);

      // optimistic append bila server kirim objek
      const newItem = res?.data;
      if (newItem?.id) {
        setResources((prev) => [newItem, ...prev]);
      }

      toast.success('Resource created successfully!');
      setAddModal(false);
      resetForm();

      // silent refresh agar akurat tanpa blank
      fetchResources({ silent: true });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create resource');
    } finally {
      setSubmitting(false);
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editModal.resource) return;
    setSubmitting(true);
    try {
      const payload = buildPayload(resourceForm);
      await api.put(`/admin/resources/${editModal.resource.id}`, payload);

      // optimistic update
      setResources((prev) =>
        prev.map((r) =>
          r.id === editModal.resource.id
            ? {
                ...r,
                name: payload.name,
                description: payload.description,
                type_name: payload.type_name,
                floor_id: payload.floor_id,
                position_x: payload.position_x,
                position_y: payload.position_y,
                is_active: payload.is_active,
              }
            : r
        )
      );

      toast.success('Resource updated successfully!');
      setEditModal({ open: false, resource: null });
      resetForm();

      // silent refresh
      fetchResources({ silent: true });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update resource');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteDialog.resource) return;
    setSubmitting(true);
    try {
      const id = deleteDialog.resource.id;

      // optimistic remove
      setResources((prev) => prev.filter((r) => r.id !== id));

      await api.delete(`/admin/resources/${id}`);
      toast.success('Resource deleted successfully!');
      setDeleteDialog({ open: false, resource: null });

      // silent refresh
      fetchResources({ silent: true });
    } catch (error) {
      // rollback kalau perlu? (opsional, karena delete gagal â†’ muat ulang)
      fetchResources({ silent: true });
      toast.error(error.response?.data?.error || 'Failed to delete resource');
    } finally {
      setSubmitting(false);
    }
  };

  /* ---------- Release booking (manual release) ---------- */
  const askRelease = (booking) => setReleaseDialog({ open: true, booking, loading: false });

  const doRelease = async () => {
    const b = releaseDialog.booking;
    if (!b?.id) return;

    setReleaseDialog((d) => ({ ...d, loading: true }));
    try {
      await api.put(`/admin/bookings/${b.id}/release`);
      toast.success('Booking released.');
      setReleaseDialog({ open: false, booking: null, loading: false });
      refreshStatus({ silent: true }).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to release booking');
      setReleaseDialog((d) => ({ ...d, loading: false }));
    }
  };
  // filter by tab
  const filteredResources = useMemo(
    () => {
      const q = searchTerm.trim().toLowerCase();
      return resources.filter((r) => {
        const typeName = (r?.type_name || '').toLowerCase();
        if (typeName !== activeTab) return false;
        if (!q) return true;
        const hay = [r.name, r.description, r.building_name, r.floor_name]
          .map((v) => String(v || '').toLowerCase())
          .join(' ');
        return hay.includes(q);
      });
    },
    [resources, activeTab, searchTerm]
  );

  /* ======= helper: bookedBy for resource (active overlap in statusWindow) ======= */
  const bookedByMap = useMemo(() => {
    const map = new Map(); // resource_id -> { username, start, end, commute }
    const now = new Date();
    for (const b of rangeBookings || []) {
      if (!isActiveBooking(b, now)) continue;
      const rid = Number(b.resource_id);
      if (!rid) continue;

      const { start, end } = normalizeTimes(b);
      // keep earliest start for the same resource (optional)
      const current = map.get(rid);
      if (!current || (start < current.start)) {
        map.set(rid, {
          bookingId: b.id,
          resourceName: b.resource_name || b.resourceName || '',
          username: b.username || b.user_name || b.user || '',
          start,
          end,
          commute: (b.commute || '').trim(),
        });
      }
    }
    return map;
  }, [rangeBookings]);

  const ResourceCard = React.memo(({ resource }) => {
    const isMenuOpen = activeDropdown === resource.id;
    const typeLower = (resource?.type_name || '').toLowerCase();

    const Icon = typeLower === 'parking' ? Car : typeLower === 'motorcycle' ? Bike : Monitor;
    const iconBg =
      typeLower === 'parking'
        ? 'bg-amber-600'
        : typeLower === 'motorcycle'
        ? 'bg-sky-600'
        : 'bg-emerald-600';

    const booked = bookedByMap.get(Number(resource.id));
    const bookedName = booked?.username || '';
    const showBookedBy = !!bookedName;

    return (
      <motion.div
        initial={false}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-10% 0px' }}
        transition={{ duration: 0.18 }}
        className={`relative ${isMenuOpen ? 'z-50' : 'z-0'}`}
      >
        <Card
          className={`bg-gradient-to-br from-gray-800 to-gray-900 border-gray-600 p-4 sm:p-6 ${
            !resource.is_active ? 'opacity-60' : ''
          } overflow-visible`}
        >
          <div className="flex items-start justify-between gap-1 sm:gap-3 mb-2 sm:mb-4">
            <div className="flex items-start gap-2 sm:gap-3 min-w-0">
              <div className={`p-1.5 sm:p-3 rounded-lg ${iconBg}`}>
                <Icon className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-white text-[11px] sm:text-lg leading-tight truncate max-w-[80px] sm:max-w-none">{resource.name}</h3>
                <p className="text-gray-400 text-[10px] sm:text-sm truncate max-w-[90px] sm:max-w-none">{resource.description || 'No description'}</p>
              </div>
            </div>

            <div className="flex flex-col items-end gap-1 shrink-0">
              <span
                className={`px-1.5 py-0.5 rounded-full text-[9px] sm:text-xs font-medium ${
                  resource.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}
              >
                {resource.is_active ? 'Active' : 'Hidden'}
              </span>

              <div className="relative">
                {/* FIX: button type + preventDefault + stopPropagation */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setActiveDropdown(isMenuOpen ? null : resource.id);
                  }}
                  className="p-1 sm:p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                  aria-label="Open actions"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>

                <AnimatePresence>
                  {isMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95, y: -10 }}
                      className="absolute right-0 top-full mt-1 w-44 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleEdit(resource);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white flex items-center space-x-2"
                      >
                        <Edit3 className="h-4 w-4" />
                        <span>Edit</span>
                      </button>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleVisibility(resource);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-gray-700 hover:text-white flex items-center space-x-2"
                      >
                        {resource.is_active ? (
                          <>
                            <Eye className="h-4 w-4" />
                            <span>Hide</span>
                          </>
                        ) : (
                          <>
                            <EyeOff className="h-4 w-4" />
                            <span>Show</span>
                          </>
                        )}
                      </button>

                      {booked?.bookingId && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            askRelease({
                              id: booked.bookingId,
                              resource_name: booked.resourceName || resource.name,
                              username: booked.username || '',
                            });
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-amber-300 hover:bg-amber-500/10 hover:text-amber-200 flex items-center space-x-2"
                        >
                          <Unlock className="h-4 w-4" />
                          <span>Release Booking</span>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDelete(resource);
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-600/10 hover:text-red-300 flex items-center space-x-2 rounded-b-lg"
                      >
                        <Trash2 className="h-4 w-4" />
                        <span>Delete</span>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>

          <div className="space-y-2 text-xs sm:text-sm text-gray-400">
            {resource.floor_name && (
              <p>
                <span className="font-medium">Floor:</span> {resource.floor_name}
              </p>
            )}
            {resource.building_name && (
              <p>
                <span className="font-medium">Building:</span> {resource.building_name}
              </p>
            )}
            {(resource.position_x || resource.position_y) ? (
              <p>
                <span className="font-medium">Position:</span> ({resource.position_x || 0},{' '}
                {resource.position_y || 0})
              </p>
            ) : null}

            {/* âœ… Booked By (active overlap within status window) */}
            {showBookedBy && (
              <div className="mt-3 bg-gray-700/40 border border-gray-600 rounded-lg p-3 text-gray-200">
                <div className="flex items-center gap-2 text-xs sm:text-sm">
                  <User className="w-4 h-4 text-purple-300" />
                  <span className="text-gray-300">Booked by:</span>
                  <span className="font-semibold text-white">{bookedName}</span>
                  <span className="ml-auto px-2 py-0.5 rounded-full text-xs bg-purple-200 text-purple-900">
                    Active
                  </span>
                </div>
                <div className="mt-2 flex items-center gap-2 text-[10px] sm:text-xs text-gray-300">
                  <Clock className="w-4 h-4 text-sky-300" />
                  <span>
                    {booked.start.toLocaleString('en-GB', { hour12: false })} “{' '}
                    {booked.end.toLocaleString('en-GB', { hour12: false })}
                  </span>
                </div>
                {booked.commute && (
                  <div className="mt-1 text-[10px] sm:text-xs text-gray-300">
                    Commute: <span className="text-gray-100 font-medium">{booked.commute}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      </motion.div>
    );
  });
  ResourceCard.displayName = 'ResourceCard';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  const tabLabel =
    activeTab === 'parking' ? 'Car' : activeTab === 'motorcycle' ? 'Motorcycle' : 'Desk';
  const addLabel =
    activeTab === 'parking'
      ? 'Car Slot'
      : activeTab === 'motorcycle'
      ? 'Motorcycle Slot'
      : 'Desk Space';

  const totalFiltered = filteredResources.length;
  const activeCount = filteredResources.filter((r) => r.is_active).length;
  const hiddenCount = totalFiltered - activeCount;

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between gap-3 flex-wrap"
      >
        <div>
          <h1 className="text-2xl font-bold text-white mb-2 flex items-center">
            <Building className="h-8 w-8 text-purple-400 mr-3" />
            Manage Resources
          </h1>
          <p className="text-gray-400">
            Add, edit, and manage car slots, motorcycle slots, and desk spaces
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => refreshStatus({ silent: false })}
            className="flex items-center gap-2"
            disabled={statusLoading}
          >
            <RefreshCcw className="h-4 w-4" />
            {statusLoading ? 'Refreshing...' : 'Refresh Status'}
          </Button>

          <Button onClick={handleAdd} className="flex items-center">
            <Plus className="h-5 w-5 mr-2" />
            Add {addLabel}
          </Button>
        </div>
      </motion.div>

      <div className="w-full max-w-md">
        <Input
          label="Search resources"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, description, floor, or building"
        />
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-800 p-1 rounded-lg">
        <button
          type="button"
          onClick={() => {
            setActiveTab('parking');
            resetForm();
          }}
          className={`flex-1 flex items-center justify-center py-3 px-4 rounded-md transition-all duration-200 ${
            activeTab === 'parking'
              ? 'bg-gradient-to-r from-amber-600 to-yellow-600 text-white shadow-lg'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }`}
        >
          <Car className="h-5 w-5 mr-2" />
          Car Resources
        </button>

        <button
          type="button"
          onClick={() => {
            setActiveTab('motorcycle');
            resetForm();
          }}
          className={`flex-1 flex items-center justify-center py-3 px-4 rounded-md transition-all duration-200 ${
            activeTab === 'motorcycle'
              ? 'bg-gradient-to-r from-sky-600 to-cyan-600 text-white shadow-lg'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }`}
        >
          <Bike className="h-5 w-5 mr-2" />
          Motorcycle Resources
        </button>

        <button
          type="button"
          onClick={() => {
            setActiveTab('desk');
            resetForm();
          }}
          className={`flex-1 flex items-center justify-center py-3 px-4 rounded-md transition-all duration-200 ${
            activeTab === 'desk'
              ? 'bg-gradient-to-r from-emerald-600 to-green-600 text-white shadow-lg'
              : 'text-gray-400 hover:text-white hover:bg-gray-700'
          }`}
        >
          <Monitor className="h-5 w-5 mr-2" />
          Desk Resources
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 md:grid-cols-3 gap-6">
        <Card className="bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-200 text-sm">Total {tabLabel} Resources</p>
              <p className="text-3xl font-bold text-white">{totalFiltered}</p>
            </div>
            {activeTab === 'parking' ? (
              <Car className="h-8 w-8 text-blue-400" />
            ) : activeTab === 'motorcycle' ? (
              <Bike className="h-8 w-8 text-blue-400" />
            ) : (
              <Monitor className="h-8 w-8 text-blue-400" />
            )}
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-green-800 to-green-900">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-200 text-sm">Active Resources</p>
              <p className="text-3xl font-bold text-white">{activeCount}</p>
            </div>
            <Eye className="h-8 w-8 text-green-400" />
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-red-800 to-red-900">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-red-200 text-sm">Hidden Resources</p>
              <p className="text-3xl font-bold text-white">{hiddenCount}</p>
            </div>
            <EyeOff className="h-8 w-8 text-red-400" />
          </div>
        </Card>
      </div>

      {/* Grid */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="grid grid-cols-3 md:grid-cols-3 lg:grid-cols-3 gap-6"
        >
          {filteredResources.map((resource) => (
            <ResourceCard key={resource.id} resource={resource} />
          ))}
        </motion.div>
      </AnimatePresence>

      {filteredResources.length === 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-12"
        >
          <div className="w-24 h-24 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
            {activeTab === 'parking' ? (
              <Car className="h-12 w-12 text-gray-500" />
            ) : activeTab === 'motorcycle' ? (
              <Bike className="h-12 w-12 text-gray-500" />
            ) : (
              <Monitor className="h-12 w-12 text-gray-500" />
            )}
          </div>
          <h3 className="text-xl font-medium text-white mb-2">No {activeTab} resources found</h3>
          <p className="text-gray-400 mb-6">
            Start by adding your first{' '}
            {activeTab === 'parking'
              ? 'car slot'
              : activeTab === 'motorcycle'
              ? 'motorcycle slot'
              : 'desk space'}
            .
          </p>
          <Button onClick={handleAdd}>Add First {addLabel}</Button>
        </motion.div>
      )}

      {/* Add Modal */}
      <Modal
        isOpen={addModal}
        onClose={() => setAddModal(false)}
        title={`Add New ${addLabel}`}
        size="md"
      >
        <form onSubmit={submitAdd} className="space-y-4">
          <Input
            label="Name"
            value={resourceForm.name}
            onChange={(e) => {
              setNameTouched(true);
              setResourceForm((prev) => ({ ...prev, name: e.target.value }));
            }}
            placeholder={`Enter ${activeTab} name (auto for Desk/Motorcycle)`}
            required
          />

          <Input
            label="Description"
            value={resourceForm.description}
            onChange={(e) =>
              setResourceForm((prev) => ({ ...prev, description: e.target.value }))
            }
            placeholder="Enter description (optional)"
          />

          {activeTab === 'desk' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Floor</label>
              <select
                value={resourceForm.floor_id}
                onChange={(e) => {
                  const fid = e.target.value;
                  const auto = nameTouched ? resourceForm.name : nextDeskNameByFloor(fid);
                  setResourceForm((prev) => ({ ...prev, floor_id: fid, name: auto }));
                }}
                className="block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              >
                <option value="">Select Floor</option>
                {floors.map((floor) => (
                  <option key={floor.id} value={floor.id}>
                    {floor.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* untuk Motorcycle/Parking tidak perlu floor */}

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Position X"
              type="number"
              value={resourceForm.position_x}
              onChange={(e) =>
                setResourceForm((prev) => ({
                  ...prev,
                  position_x: parseInt(e.target.value, 10) || 0,
                }))
              }
              placeholder="0"
            />
            <Input
              label="Position Y"
              type="number"
              value={resourceForm.position_y}
              onChange={(e) =>
                setResourceForm((prev) => ({
                  ...prev,
                  position_y: parseInt(e.target.value, 10) || 0,
                }))
              }
              placeholder="0"
            />
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="is_active"
              checked={resourceForm.is_active}
              onChange={(e) =>
                setResourceForm((prev) => ({ ...prev, is_active: e.target.checked }))
              }
              className="w-4 h-4 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500"
            />
            <label htmlFor="is_active" className="text-sm text-gray-300">
              Make resource visible to users
            </label>
          </div>

          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setAddModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" loading={submitting} className="flex-1">
              Add Resource
            </Button>
          </div>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={editModal.open}
        onClose={() => setEditModal({ open: false, resource: null })}
        title="Edit Resource"
        size="md"
      >
        <form onSubmit={submitEdit} className="space-y-4">
          <Input
            label="Name"
            value={resourceForm.name}
            onChange={(e) => {
              setNameTouched(true);
              setResourceForm((prev) => ({ ...prev, name: e.target.value }));
            }}
            placeholder="Enter resource name"
            required
          />

          <Input
            label="Description"
            value={resourceForm.description}
            onChange={(e) =>
              setResourceForm((prev) => ({ ...prev, description: e.target.value }))
            }
            placeholder="Enter description (optional)"
          />

          {resourceForm.type === 'desk' && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Floor</label>
              <select
                value={resourceForm.floor_id}
                onChange={(e) =>
                  setResourceForm((prev) => ({ ...prev, floor_id: e.target.value }))
                }
                className="block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="">Select Floor</option>
                {floors.map((floor) => (
                  <option key={floor.id} value={floor.id}>
                    {floor.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Position X"
              type="number"
              value={resourceForm.position_x}
              onChange={(e) =>
                setResourceForm((prev) => ({
                  ...prev,
                  position_x: parseInt(e.target.value, 10) || 0,
                }))
              }
              placeholder="0"
            />
            <Input
              label="Position Y"
              type="number"
              value={resourceForm.position_y}
              onChange={(e) =>
                setResourceForm((prev) => ({
                  ...prev,
                  position_y: parseInt(e.target.value, 10) || 0,
                }))
              }
              placeholder="0"
            />
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="edit_is_active"
              checked={resourceForm.is_active}
              onChange={(e) =>
                setResourceForm((prev) => ({ ...prev, is_active: e.target.checked }))
              }
              className="w-4 h-4 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500"
            />
            <label htmlFor="edit_is_active" className="text-sm text-gray-300">
              Make resource visible to users
            </label>
          </div>

          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setEditModal({ open: false, resource: null })}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button type="submit" loading={submitting} className="flex-1">
              Update Resource
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, resource: null })}
        onConfirm={confirmDelete}
        title="Delete Resource"
        message={`Are you sure you want to delete ${deleteDialog.resource?.name}? This action cannot be undone and will cancel all future bookings for this resource.`}
        type="danger"
        confirmText="Delete Resource"
        loading={submitting}
      />

      <ConfirmDialog
        isOpen={releaseDialog.open}
        onClose={() => setReleaseDialog({ open: false, booking: null, loading: false })}
        onConfirm={doRelease}
        title="Release Booking"
        message={`Release booking "${releaseDialog.booking?.resource_name}" by ${
          releaseDialog.booking?.username || '-'
        }? This will free the slot for others today.`}
        type="danger"
        confirmText="Release"
        loading={releaseDialog.loading}
      />
    </div>
  );
}

export default AdminResources;